import React, { useEffect } from 'react';
import {
  SkeletonBlock,
  SkeletonLine,
  Typography,
} from '@one-thd/sui-atomic-components';
import { Warning } from '@one-thd/sui-icons';
import useFetchState from '@src/hooks/useFetchState';
import { IScheduledPickupsRequest } from '@src/models/api';
import {
  setPickupDates,
  setFetchScheduledPickups,
  setShowCalendar,
  useReturnStore,
} from '@src/stores/returnStore';
import { handleScheduledPickupsCall } from '@src/utils/api-helpers';
import { getCurrentReturnET } from '@src/utils/returnToken';
import { TagPostPageError } from '@src/utils/analytics-tagging';
import { LORP_SCHEDULE_PICKUP_ERROR_MESSAGE } from '@src/constants/analytics';
import { LORPPickupCostAlert } from './LORPPickupCostAlert';
import { LORPPickupDateTiles } from './LORPPickupDateTiles';
import { LORPPickupDatePicker } from './LORPPickupDatePicker';

const LORPSchedulePickupSkeleton = () => (
  <div className="sui-mt-4" data-testid="lorp-schedule-pickup-skeleton">
    <SkeletonLine variant="heading" />
    <div className="sui-my-4">
      <SkeletonLine numberOfLines={2} />
    </div>
    <SkeletonBlock />
  </div>
);

interface LORPSchedulePickupStepProps {
  onLoadingChange?: (isLoading: boolean) => void;
}

export const LORPSchedulePickupStep = ({
  onLoadingChange,
}: LORPSchedulePickupStepProps) => {
  const showCalendar = useReturnStore((state) => state.showCalendar);
  const fetchScheduledPickups = useReturnStore(
    (state) => state.fetchScheduledPickups,
  );
  const pickupDates = useReturnStore((state) => state.pickupDates);
  const orderDetailsV2 = useReturnStore((state) => state.orderDetailsV2);
  const returnShipments = useReturnStore((state) => state.returnShipments);
  const lorpShipment = returnShipments?.find(
    (shipment) => shipment.shipmentType === 'PICKUP',
  );
  const zipCode = lorpShipment?.lorpContactDetails?.address?.zipCode || '';
  const net = orderDetailsV2?.net || '';

  const { loading, error, isFetched, fetchData } = useFetchState<boolean>();
  const isLoadingScheduledPickups = loading || !isFetched;

  useEffect(() => {
    onLoadingChange?.(isLoadingScheduledPickups);
  }, [isLoadingScheduledPickups, onLoadingChange]);

  const loadScheduledPickups = async () => {
    if (!fetchScheduledPickups) {
      return true;
    }

    // Resolved at the point of the call, like every other consumer of the
    // token, rather than held from render. This particular request goes out on
    // mount, so the two are equivalent here today — the consistency is the
    // point, so that nothing has to be revisited if this ever fetches again in
    // response to something the customer does.
    const request: IScheduledPickupsRequest = {
      zipCode,
      et: getCurrentReturnET(),
      net,
    };
    const response = await handleScheduledPickupsCall(request);
    if (response?.error) {
      // Preserve the backend description so the hook error state / logs stay
      // actionable. The API-failure analytics tag (with the backend code) has
      // already fired inside handleScheduledPickupsCall; this throw only drives
      // the error UI and skips the empty-dates tag below to avoid a duplicate.
      throw new Error(
        response.error.description || 'Scheduled pickups API failed',
      );
    }
    if (!response?.data?.length) {
      TagPostPageError({
        type: 'error',
        pageSection: 'large item pickup schedule',
        message: LORP_SCHEDULE_PICKUP_ERROR_MESSAGE,
      });
      throw new Error('No scheduled pickups found');
    }
    setShowCalendar(false);
    setPickupDates(response.data);
    // Will not fetch scheduled pickups for LORP on future renders unless customer has gone back to contact details
    setFetchScheduledPickups(false);
    return true;
  };

  useEffect(() => {
    fetchData(() => loadScheduledPickups());
  }, []);

  if (isLoadingScheduledPickups) {
    return <LORPSchedulePickupSkeleton />;
  }

  if (error || !pickupDates?.length) {
    return (
      <>
        <LORPPickupCostAlert />
        <div className="sui-text-center">
          <div
            className="sui-inline-flex sui-h-16 sui-w-16 sui-items-center sui-justify-center sui-rounded-full sui-border-4 sui-border-warning"
            aria-hidden
          >
            <Warning color="warning" size="large" />
          </div>
          <Typography variant="h4">
            There was an issue finding an available pickup date
          </Typography>
        </div>
        <div className="sui-mt-4">
          <Typography>
            To check for additional dates, submit this return request and our
            carrier will contact you and schedule a custom date.
          </Typography>
        </div>
      </>
    );
  }

  return (
    <>
      <LORPPickupCostAlert />
      <LORPPickupDateTiles />
      {showCalendar && <LORPPickupDatePicker />}
    </>
  );
};
